/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* ===========================================================================
   Aroma Country — custom-order / non-refundable popup for large diffuser-oil
   sizes (24 oz. & 1 Gallon). Re-added for Focal v13.0.0 on 2026-06-25.
   Fires a dismissible popup when a 24 oz. or 1 Gallon size is selected on a
   product page. Uses Focal's documented 'variant:changed' event because Focal
   13 stores the variant id as the radio value (the old value-text match broke).
   Only diffuser oils use those size values, so other product lines never fire.
   To remove: delete everything from this comment block to the end of the file.
   =========================================================================== */
(function(){
  "use strict";
  var RE = /24[ ]*oz|gallon/i;
  var EX = /24[ ]*oz[.]?|1[ ]*gallon|gallon/i;
  var overlay = null, sizeEl = null, lastSize = null, armed = false;
  setTimeout(function(){ armed = true; }, 700);

  function build(){
    if (overlay) return;
    var style = document.createElement("style");
    style.textContent =
      ".acno-overlay{position:fixed;top:0;left:0;right:0;bottom:0;z-index:99999;display:none;align-items:center;justify-content:center;background:rgba(20,20,20,.55);padding:20px;}" +
      ".acno-overlay.is-open{display:flex;}" +
      ".acno-modal{background:#fff;color:#222;max-width:440px;width:100%;border-radius:10px;box-shadow:0 12px 40px rgba(0,0,0,.25);padding:30px 26px 24px;text-align:center;position:relative;font-family:inherit;}" +
      ".acno-modal *{box-sizing:border-box;}" +
      ".acno-badge{display:inline-block;font-size:11px;letter-spacing:.08em;text-transform:uppercase;font-weight:700;color:#8a5a00;background:#fdf3e1;border:1px solid #f0d9ab;border-radius:999px;padding:5px 12px;margin-bottom:14px;}" +
      ".acno-title{font-size:20px;line-height:1.25;font-weight:700;margin:0 0 10px;}" +
      ".acno-body{font-size:15px;line-height:1.55;margin:0 0 20px;color:#444;}" +
      ".acno-body strong{color:#222;}" +
      ".acno-btn{-webkit-appearance:none;appearance:none;border:0;cursor:pointer;background:#1a1a1a;color:#fff;font-size:15px;font-weight:600;border-radius:8px;padding:13px 22px;width:100%;}" +
      ".acno-btn:hover{opacity:.9;}" +
      ".acno-close{position:absolute;top:8px;right:12px;background:none;border:0;font-size:24px;line-height:1;cursor:pointer;color:#999;padding:4px 8px;}" +
      ".acno-close:hover{color:#333;}";
    document.head.appendChild(style);

    overlay = document.createElement("div");
    overlay.className = "acno-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Custom order notice");
    overlay.innerHTML =
      '<div class="acno-modal">' +
        '<button type="button" class="acno-close" data-acno-close aria-label="Close">&times;</button>' +
        '<span class="acno-badge">Custom Order</span>' +
        '<p class="acno-title">This size is made to order</p>' +
        '<p class="acno-body">The <strong><span data-acno-size>24&nbsp;oz.</span></strong> size is custom-blended to order. Because it&rsquo;s made on demand, this size is <strong>final sale &mdash; non-refundable</strong> and cannot be returned or exchanged.<br><br>Our 4&nbsp;oz. and 8.5&nbsp;oz. sizes are unaffected.</p>' +
        '<button type="button" class="acno-btn" data-acno-close>I understand</button>' +
      '</div>';
    document.body.appendChild(overlay);
    sizeEl = overlay.querySelector("[data-acno-size]");

    overlay.addEventListener("click", function(e){
      if (e.target === overlay || (e.target.hasAttribute && e.target.hasAttribute("data-acno-close"))) closeModal();
    });
  }

  function openModal(size){
    build();
    if (sizeEl && size) sizeEl.textContent = String(size).trim();
    overlay.classList.add("is-open");
    document.documentElement.style.overflow = "hidden";
    var btn = overlay.querySelector(".acno-btn");
    if (btn) { try { btn.focus(); } catch (e) {} }
  }
  function closeModal(){
    if (!overlay) return;
    overlay.classList.remove("is-open");
    document.documentElement.style.overflow = "";
  }

  function trigger(text){
    if (!armed || text == null) return;
    text = String(text);
    if (!RE.test(text)) { lastSize = null; return; }
    var m = text.match(EX);
    var size = m ? m[0] : text.trim();
    if (size !== lastSize) { lastSize = size; openModal(size); }
  }

  // Primary: Focal's documented variant:changed event (carries the option text).
  document.addEventListener("variant:changed", function(e){
    var v = e && e.detail && e.detail.variant;
    if (!v) return;
    var cands = [];
    if (v.options && v.options.length) cands = cands.concat(v.options);
    [v.option1, v.option2, v.option3, v.title].forEach(function(x){ if (x) cands.push(x); });
    var hit = null;
    for (var i = 0; i < cands.length; i++) { if (RE.test(String(cands[i]))) { hit = String(cands[i]); break; } }
    if (hit) trigger(hit); else lastSize = null;
  });

  // Fallback: native change on an option input (Focal 13 stores the variant id
  // as the input value, so read the selected label/option text instead).
  document.addEventListener("change", function(e){
    var t = e.target;
    if (!t || !t.name || !/^option/i.test(t.name)) return;
    var text = "";
    if (t.tagName === "SELECT") {
      var o = t.options && t.options[t.selectedIndex];
      text = o ? o.textContent : "";
    } else if (t.type === "radio") {
      if (!t.checked) return;
      var lab = t.id ? document.querySelector('label[for="' + ((window.CSS && CSS.escape) ? CSS.escape(t.id) : t.id) + '"]') : null;
      text = lab ? lab.textContent : (t.getAttribute("aria-label") || "");
    }
    trigger(text);
  }, true);

  document.addEventListener("keydown", function(e){
    if ((e.key === "Escape" || e.keyCode === 27) && overlay && overlay.classList.contains("is-open")) closeModal();
  });
})();

/* Aroma Country — heading hygiene. Re-added for Focal v13.0.0 (the update wiped the old inline Klaviyo H1->div and Judge.me heading->p swaps). Demotes app-injected duplicate headings so every page keeps a single semantic H1. */
(function(){
  "use strict";
  function swapKlaviyo(){
    document.querySelectorAll('.klaviyo-form h1, [class*="needsclick"] h1, [class*="kl-private"] h1').forEach(function(h1){
      var d = document.createElement("div");
      for (var i = 0; i < h1.attributes.length; i++) d.setAttribute(h1.attributes[i].name, h1.attributes[i].value);
      d.innerHTML = h1.innerHTML;
      if (h1.parentNode) h1.parentNode.replaceChild(d, h1);
    });
  }
  function swapJudgeMe(){
    document.querySelectorAll('.jdgm-widget h1, .jdgm-widget h2, .jdgm-widget h3, .jdgm-widget h4, .jdgm-widget h5, .jdgm-widget h6, h2.jdgm-rev-widg__title, .jdgm-rev-widg h2, .jdgm-rev-widg h3, .shopify-section--product-recommendations h3, .product-recommendations h3').forEach(function(h){
      var p = document.createElement("p");
      for (var i = 0; i < h.attributes.length; i++) p.setAttribute(h.attributes[i].name, h.attributes[i].value);
      p.innerHTML = h.innerHTML;
      if (h.parentNode) h.parentNode.replaceChild(p, h);
    });
  }
  function run(){ try { swapKlaviyo(); } catch (e) {} try { swapJudgeMe(); } catch (e) {} }
  if (document.readyState === "loading") { document.addEventListener("DOMContentLoaded", run); } else { run(); }
  // Batch DOM changes: run at most once per frame instead of sweeping the whole page on every insertion (2026-10-03, page speed).
  var queued = false;
  function schedule(){
    if (queued) return;
    queued = true;
    (window.requestAnimationFrame || function(cb){ return setTimeout(cb, 16); })(function(){ queued = false; run(); });
  }
  function startObserver(){
    var obs = new MutationObserver(function(muts){ for (var i = 0; i < muts.length; i++) { if (muts[i].addedNodes && muts[i].addedNodes.length) { schedule(); break; } } });
    obs.observe(document.body, { childList: true, subtree: true });
  }
  if (document.body) startObserver(); else document.addEventListener("DOMContentLoaded", startObserver);
})();

/* === Aroma Country — restore "Build Your Own" scroll. Focal v13 wiped the #build-your-own scroll script from theme.liquid; re-added here (custom.js survives theme updates). Clicking the BYP "Build Your Own" card (#build-your-own, href=javascript:void(0)) does a slow, smooth scroll to the "Before You Order" segment (#section-before-order; falls back to #byp-body) so the customer reads the What-You-Receive / How-To-Order info. Uses a time-based setInterval tween (not scrollIntoView) because Focal v13 bundles seamless-scroll-polyfill, which makes scrollIntoView({behavior:"smooth"}) unreliable here. === */
(function(){
  "use strict";
  function slowScrollTo(targetY, duration){
    var startY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var diff = targetY - startY;
    if (Math.abs(diff) < 2) { window.scrollTo(0, targetY); return; }
    var startTime = Date.now();
    function ease(t){ return t < 0.5 ? 2*t*t : -1 + (4 - 2*t)*t; } // easeInOutQuad
    var timer = setInterval(function(){
      var p = Math.min((Date.now() - startTime) / duration, 1);
      window.scrollTo(0, Math.round(startY + diff * ease(p)));
      if (p >= 1) clearInterval(timer);
    }, 16);
  }
  document.addEventListener("click", function(e){
    var card = (e.target && e.target.closest) ? e.target.closest("#build-your-own") : null;
    if (!card) return;
    var target = document.getElementById("section-before-order") || document.getElementById("byp-body");
    if (!target) return;
    e.preventDefault();
    var top = (window.pageYOffset || document.documentElement.scrollTop || 0) + target.getBoundingClientRect().top;
    slowScrollTo(top, 1100);
  }, true);
})();